import { Button } from "@/components/Button";
import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";
import Layout from "@/components/Layout";
import {
  AssurantQuoteV2,
  AssurantQuoteV2PaymentSchedule
} from "@/types/Insurance";
import formatCurrency from "@/utils/formatCurrency";
import React, { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useLocation } from "react-router";
import { useUpdateInsuranceV2Quote } from "./insurance-quote-v2";
import useLivlyUser from "@/context/UserProvider";
import { OnboardingStatus, OnboardingStatusEnum } from "@/types/User";
import { useGetStatusTypeDetails, usePostService } from "./landing";
import { isNativeAppVersion, returnToNative } from "@/utils/nativeAppHelpers";
import { Spinner } from "@/components/Spinner";
import { useQueryClient } from "@tanstack/react-query";

interface Consent {
  textAlerts: boolean;
  emailDocs: boolean;
}

const Summary: React.FC = () => {
  const { user } = useLivlyUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { label: string; isInsuranceOnly: boolean; noQuestions?: boolean };
  };
  const { data, selectionMetaData, updateMetaData, refetch } =
    useOutletContext<InsuranceQuoteContext>();
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);
  const details = useGetStatusTypeDetails(OnboardingStatusEnum.insurance, user);
  const { updateOnboardingStatus } = usePostService(
    user.userId,
    user.propertyUnitLeaseId.toString(),
    details.onboardingStatus
  );

  // Memoize plan and parameter extraction for performance and clarity
  const {
    selectedPlan,
    isFullPay,
    isQuarterlyPay,
    liabilityAmount,
    personalPropertyAmount,
    deductible,
    replacementCost,
    riskState,
    hurricaneDeductible,
    coverageSewerBackup,
    earthquake,
    fraudExpenseProtection,
    isPaperless
  } = useMemo(() => {
    const quote = data as AssurantQuoteV2;
    const selectedPlan = getSelectedPlan(quote);
    const isFullPay = selectedPlan?.numberOfPayments === 1;
    const isQuarterlyPay = selectedPlan?.numberOfPayments === 4;
    const parameters = quote.quoteResponse.coverageDetails.parameters;
    return {
      selectedPlan,
      isFullPay,
      isQuarterlyPay,
      liabilityAmount: getValue(parameters, "liabilityAmount"),
      personalPropertyAmount: getValue(parameters, "personalPropertyAmount"),
      deductible: getValue(parameters, "deductible"),
      replacementCost: getValue(parameters, "replacementCost") as string,
      riskState:
        quote.quoteResponse.policyHolderDetails?.insured?.address?.state,
      hurricaneDeductible: getValue(parameters, "hurricaneDeductible"),
      coverageSewerBackup: getValue(parameters, "waterBackup"),
      earthquake: getValue(parameters, "earthquake"),
      fraudExpenseProtection: getValue(parameters, "identityFraud"),
      isPaperless: quote?.selectionMetaData?.isPaperless ?? true
    };
  }, [data]);

  const [consent, setConsent] = useState<Consent>({
    textAlerts: false,
    emailDocs: isPaperless
  });
  const [status, setStatus] = useState<
    "adding" | "removing" | "keeping" | "pending"
  >("pending");
  const handleCheckboxChange = (id: keyof Consent) => {
    setConsent((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const total = useMemo(() => {
    if (!selectedPlan) return 0;
    if (isFullPay) return Number(selectedPlan.downPaymentAmount) || 0;
    return (
      Number(selectedPlan.installmentAmount || 0) +
      Number(selectedPlan.serviceFee || 0)
    );
  }, [selectedPlan, isFullPay]);

  const consentOptions = [
    {
      id: "textAlerts" as keyof Consent,
      label: "I’d like to receive text alerts on my phone",
      description:
        "We’ll send you texts keeping you up to date on your policy, never sales pitches."
    },
    {
      id: "emailDocs" as keyof Consent,
      label: "I’d like to receive all policy and billing documents by email."
    }
  ];

  const onAddPlan = async () => {
    const quote = data as AssurantQuoteV2;
    try {
      setStatus("adding");
      //@ts-ignore
      updateMetaData({
        ...selectionMetaData,
        isPlanAdded: true,
        numberOfPayments: quote.selectionMetaData?.numberOfPayments ?? "11",
        okToText: consent.textAlerts,
        isPaperless: consent.emailDocs
      });

      await updateAssurantQuoteMutation.mutateAsync({
        newRequest: null,
        SelectionMetaData: {
          ...selectionMetaData,
          isPlanAdded: true,
          numberOfPayments: quote.selectionMetaData?.numberOfPayments ?? "11",
          okToText: consent.textAlerts,
          isPaperless: consent.emailDocs,
          uwAnswers: selectionMetaData?.uwAnswers ?? ""
        }
      });

      const notes = `$${selectedPlan?.downPaymentAmount} will be added at checkout`;
      const newOnboardingStatus: OnboardingStatus = {
        ...details.onboardingStatus,
        isCompleted: true,
        notes
      };
      await updateOnboardingStatus(newOnboardingStatus);
      queryClient.invalidateQueries(["cart"]);
      refetch();
      //track analytics

      if (isNativeAppVersion()) {
        returnToNative();
        return;
      }

      navigate("..");
    } catch (e) {
      setStatus("pending");
    }
  };

  const onCheckout = async () => {
    const quote = data as AssurantQuoteV2;
    try {
      setStatus("adding");
      //@ts-ignore
      updateMetaData({
        ...selectionMetaData,
        isPlanAdded: true,
        numberOfPayments: quote.selectionMetaData?.numberOfPayments ?? "11",
        okToText: consent.textAlerts,
        isPaperless: consent.emailDocs
      });

      await updateAssurantQuoteMutation.mutateAsync({
        newRequest: {
          ...quote.quoteRequest
        },
        SelectionMetaData: {
          ...selectionMetaData,
          isPlanAdded: true,
          numberOfPayments: quote?.selectionMetaData?.numberOfPayments ?? "11"
        }
      });
      refetch();
      queryClient.invalidateQueries(["insurance", "quote"]);

      //track analytics

      navigate("../insurance-quote/checkout");
    } catch (e) {
      setStatus("pending");
    }
  };

  return (
    <Layout
      title="Summary"
      back={{
        label: location.state?.label || "Liability Questions",
        to: location.state?.noQuestions ? "../insurance-quote" : -1
      }}
    >
      <div className="max-w-4xl mx-auto">
        <h2 className="font-medium text-lg" data-test-id="summary">
          Summary
        </h2>

        <div className="mt-4">
          <div className="flex justify-between text-xl font-medium border-b border-gray-300 pb-8">
            <p>
              {isFullPay ? "Yearly" : isQuarterlyPay ? "Quarterly" : "Monthly"}{" "}
              Total
            </p>
            <p>{formatCurrency(total)}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-8">
            <SummaryItem
              label="Personal Liability"
              value={formatCurrency(Number(liabilityAmount))}
            />
            <SummaryItem
              label="Personal Belongings"
              value={formatCurrency(Number(personalPropertyAmount))}
              align="right"
            />
            <SummaryItem
              label="Deductible"
              value={formatCurrency(Number(deductible))}
            />
            <SummaryItem
              label="Replacement Coverage"
              value={replacementCost === "Y" ? "Included" : "Excluded"}
              align="right"
            />
            <SummaryItem
              label="Water/Sewer Back Up"
              value={coverageSewerBackup === "Y" ? "Included" : "Excluded"}
            />
            <SummaryItem
              label="Fraud Expense Protection"
              value={fraudExpenseProtection === "Y" ? "Included" : "Excluded"}
              align="right"
            />
            {hurricaneDeductible && riskState === "FL" && (
              <SummaryItem
                label="Hurricane Deductible"
                value={formatCurrency(Number(hurricaneDeductible))}
              />
            )}
            {riskState === "NY" && (
              <SummaryItem label="Flood" value={"Included"} />
            )}
            {(riskState === "WA" || riskState === "CA") && (
              <SummaryItem
                label="Earthquake"
                value={earthquake === "Y" ? "Included" : "Excluded"}
              />
            )}
          </div>

          {/* Electronic Consent */}
          <div className="mt-8 mb-52 md:mb-2">
            {consentOptions.map((item) => (
              <div key={item.id} className="flex items-start gap-4 mt-4">
                <input
                  type="checkbox"
                  id={item.id}
                  className="mt-1 cursor-pointer"
                  checked={consent[item.id]}
                  onChange={() => handleCheckboxChange(item.id)}
                />
                <label
                  htmlFor={item.id}
                  className="text-sm md:text-base text-gray-700"
                >
                  <span>{item.label}</span>
                  {item.description && (
                    <span className="text-gray-500 text-xs md:text-sm italic">
                      <br />
                      {item.description}
                    </span>
                  )}
                </label>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="max-w-4xl mx-auto fixed bottom-0 left-4 right-4 flex flex-col items-stretch m-4 bg-white border border-gray-100 rounded-lg shadow-lg md:flex-row md:justify-between md:items-baseline md:left-[272px] drop-shadow-lg">
            <div className="flex flex-col items-center justify-between w-full p-4 border-t border-gray-200 md:flex-row md:border-none">
              <div className="flex flex-col items-center justify-center mr-4 md:items-baseline">
                <p className="text-xs font-light text-center md:mr-2 md:text-left">
                  I acknowledge that I have read and understand all applicable
                  state disclosures, fraud notices, and notice of cancellation.
                </p>
              </div>
              <div className="flex items-center w-full gap-4 mt-6 md:mt-0 md:w-auto">
                {location.state?.isInsuranceOnly ? (
                  <Button
                    color="secondary"
                    className="flex-1 w-full md:flex-auto whitespace-nowrap"
                    disabled={status === "adding"}
                    onClick={onCheckout}
                  >
                    {status === "adding" && <Spinner />}
                    Checkout
                  </Button>
                ) : (
                  <Button
                    data-test-id={`next-button`}
                    color="secondary"
                    onClick={onAddPlan}
                    disabled={status === "adding"}
                    className="flex items-center flex-1 w-full gap-2 md:w-auto whitespace-nowrap"
                  >
                    {status === "adding" && <Spinner />}
                    {status === "adding" ? "Adding Plan" : "Add Plan"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const SummaryItem: React.FC<{
  label: string;
  value: string;
  align?: "left" | "right";
}> = ({ label, value, align = "left" }) => (
  <div className={align === "right" ? "text-right" : ""}>
    <h3 className="text-sm md:text-base text-gray-400 mb-1">{label}</h3>
    <p className="text-sm md:text-base font-medium">{value}</p>
  </div>
);

function getValue(
  data: { name: string; value: any }[] = [],
  paramName: string
): string | number | number[] | null | undefined {
  return data.find((p) => p.name === paramName)?.value;
}

function getSelectedPlan(
  quote: AssurantQuoteV2
): AssurantQuoteV2PaymentSchedule | undefined {
  const { transactionDetails } = quote.quoteResponse;
  const selectedNumberOfPayments =
    Number(quote?.selectionMetaData?.numberOfPayments) || 11;
  return (
    transactionDetails.payments.schedules.find(
      (s) => s.numberOfPayments === selectedNumberOfPayments
    ) ||
    transactionDetails.payments.schedules.find(
      (s) => s.numberOfPayments === 1
    ) ||
    transactionDetails.payments.schedules.find((s) => s.numberOfPayments === 4)
  );
}

export default Summary;
